import { v4 as uuid4 } from 'uuid';

interface ElementCount {
  element: HTMLElement
  count: number
}

/**
 * Stores references to elements to make it easier to count
 * occurences of some kind of event for a list of elements.
 *
 * @example
 *   const elementCount = new ElementCountMap();
 *   elementCount.isEmpty(); // > true
 *   const element = document.getElementById(...);
 *   elementCount.increment(element); // > 1
 *   elementCount.isEmpty(); // > false
 *   elementCount.increment(element, 2); // > 3
 *   elementCount.decremenet(element, 3); // > 0
 *   elementCount.isEmpty(); // > true
 */
export default class ElementCountMap {
  innerMap: { [key: string]: ElementCount } = {};

  /**
   * Increase the count for a specific element in the count map.
   * If the element is already stored, the count is increased by
   * the `amount`. If not, a reference to the element is stored
   * with the `amount` as the starting count.
   *
   * @param element
   * @param amount
   */
  increment = (element: HTMLElement, amount = 1) => {
    const elementCount = Object.values(this.innerMap).find(
      elementCount => elementCount.element === element,
    );
    if (elementCount) {
      elementCount.count += amount;
      return elementCount.count;
    }
    else {
      this.innerMap[uuid4()] = { element, count: amount };
      return amount;
    }
  };

  /**
   * Decrease the count for a specific element in the count map,
   * if a reference to the element is stored there. If the count
   * for the element is decreased to or below 0, the reference
   * to the element is removed.
   *
   * @param element
   * @param amount
   */
  decrement = (element: HTMLElement, amount = 1) => {
    const [key, elementCount] = Object.entries(this.innerMap).find(
      ([_, elementCount]) => elementCount.element === element,
    ) || [undefined, undefined];
    if (elementCount) {
      elementCount.count = Math.max(elementCount.count - amount, 0);
      if (elementCount.count <= 0) {
        delete this.innerMap[key];
      }
      return elementCount.count;
    }
    return 0;
  };

  /**
   * Returns `true` if there are no references to any elements in
   * the inner map.
   */
  isEmpty = () => Object.keys(this.innerMap).length === 0;
}
